import { Link } from "react-router-dom";
import { Reveal, Stagger, item } from "@/components/Reveal";
import { motion } from "framer-motion";
import { Btn } from "@/components/Btn";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import { CTA, BRAND } from "@/lib/brand";import { Heart, Zap, MapPin, Facebook, Star, Phone } from "lucide-react";

const THREADS = [
  { icon: MapPin, t: "Owner run and local", d: "We are not a faceless franchise. Skedaddle is owned and run right here in the Lowcountry, so when you call, you get a neighbor who actually cares about doing the job right." },
  { icon: Zap, t: "Fast, friendly, upfront", d: "We show up when we say we will, we tell you the plan before we lift a thing, and we do it all with a smile. No pressure, no sneaky fees, no mess left behind." },
  { icon: Heart, t: "The Skedaddle way", d: "Junk hauling should not be stressful or stuffy. We wanted a name that felt as quick and cheerful as the service, and one call and it skedaddles was born." },
];

export default function About() {
  return (
    <>
      <Seo path="/about" title="About Us" description="Skedaddle Junk Removal is an owner run, local junk hauling crew serving the Lowcountry of South Carolina. Fast, friendly and upfront." />
      <PageHero center eyebrow="Our story" title="Hi, we are Skedaddle!" subtitle="A local crew on a mission to make junk disappear fast, with a smile and zero fuss." />

      <section className="bg-white py-16 md:py-24 border-b-2 border-charcoal">
        <div className="mx-auto max-w-3xl px-4 md:px-8 text-center">
          <Reveal>
            <h2 className="font-heading font-bold text-3xl md:text-4xl tracking-tight">Junk removal that feels easy!</h2>
            <div className="mt-4 space-y-4 font-body text-lg text-charcoal/75">
              <p>Skedaddle started with a simple idea. Getting rid of clutter should be quick, friendly and honest. No confusing runaround, no waiting around all day, and definitely no surprise charges at the curb.</p>
              <p>So we built a junk removal service around speed and good manners. You point at what needs to go, we do the heavy lifting, sweep up after ourselves and skedaddle. You get your garage, patio or property back and the rest of your day too!</p>
              <p>We are proud to serve homes, landlords and businesses all across Charleston, Berkeley and Dorchester counties. This is our backyard, and we treat every job like it belongs to a neighbor. Because it does!</p>
            </div>
          </Reveal>
        </div>
        <div className="mx-auto max-w-5xl px-4 md:px-8 mt-14" data-testid="about-owners">
          <Reveal className="text-center">
            <p className="font-heading font-semibold text-teal text-lg">The owners</p>
            <h2 className="font-heading font-bold text-3xl md:text-4xl tracking-tight mt-1">Meet Chad and Nathan.</h2>
          </Reveal>
          <div className="mt-10 mx-auto max-w-4xl grid sm:grid-cols-2 items-start gap-12">
            <Reveal delay={0.1}>
              <figure className="text-center" data-testid="owner-chad">
                <div className="mx-auto w-full max-w-sm rounded-[2rem] border-2 border-charcoal shadow-brut overflow-hidden aspect-[4/3]">
                  <img src="/owner-photo.webp" alt="Chad, owner of Skedaddle Junk Removal, with his dogs Zeus and Chico" width="1000" height="750" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                </div>
                <figcaption className="mt-5 mx-auto max-w-xs">
                  <h3 className="font-heading font-bold text-2xl">Chad</h3>
                  <p className="font-heading font-semibold text-coral">Owner</p>
                  <a href="tel:+18435180021" className="mt-2 inline-flex items-center gap-2 font-heading font-bold text-lg text-teal hover:underline" data-testid="owner-chad-phone"><Phone className="w-4 h-4" />843-518-0021</a>
                  <p className="mt-2 font-body text-charcoal/70">A born and bred South Carolinian, so this is home. Dog dad with his 2 sidekicks, Zeus and Chico.</p>
                </figcaption>
              </figure>
            </Reveal>
            <Reveal delay={0.2}>
              <figure className="text-center" data-testid="owner-nathan">
                <div className="mx-auto w-1/2 max-w-[12rem] rounded-[2rem] border-2 border-charcoal shadow-brut overflow-hidden aspect-[2/3]">
                  <img src="/team/nathan.webp" alt="Nathan, co-owner of Skedaddle Junk Removal" width="320" height="479" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                </div>
                <figcaption className="mt-5 mx-auto max-w-xs">
                  <h3 className="font-heading font-bold text-2xl">Nathan</h3>
                  <p className="font-heading font-semibold text-coral">Co-Owner</p>
                  <a href="tel:+18434995108" className="mt-2 inline-flex items-center gap-2 font-heading font-bold text-lg text-teal hover:underline" data-testid="owner-nathan-phone"><Phone className="w-4 h-4" />843-499-5108</a>
                  <p className="mt-2 font-body text-charcoal/70">Grew up right here in the Lowcountry. A seriously hard worker with A+ customer service, from the first hello to the final sweep.</p>
                </figcaption>
              </figure>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-cream py-16 md:py-24 border-b-2 border-charcoal">
        <div className="mx-auto max-w-5xl px-4 md:px-8">
          <Reveal><h2 className="font-heading font-bold text-3xl md:text-4xl tracking-tight text-center">What makes us tick.</h2></Reveal>
          <Stagger className="mt-10 grid gap-5 md:grid-cols-3">
            {THREADS.map((x) => (
              <motion.div key={x.t} variants={item} className="bg-white border-2 border-charcoal rounded-3xl p-7 shadow-brut">
                <div className="w-14 h-14 rounded-2xl border-2 border-charcoal bg-teal text-cream flex items-center justify-center mb-4"><x.icon className="w-7 h-7" /></div>
                <h3 className="font-heading font-bold text-2xl">{x.t}</h3>
                <p className="mt-2 font-body text-charcoal/70">{x.d}</p>
              </motion.div>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="bg-white py-16 md:py-24 text-center">
        <div className="mx-auto max-w-2xl px-4 md:px-8">
          <h2 className="font-heading font-bold text-3xl md:text-4xl tracking-tight">Ready to make some junk skedaddle?</h2>
          <p className="mt-3 font-body text-lg text-charcoal/70">Free quotes, friendly crew, no pressure. Let us take a look!</p>
          <div className="mt-8"><Btn to="/contact" variant="primary">{CTA.quote}</Btn></div>
          {BRAND.reviewFbUrl && (
            <div className="mt-12 pt-10 border-t-2 border-charcoal/10">
              <h3 className="font-heading font-bold text-2xl">Loved your Skedaddle experience?</h3>
              <p className="mt-2 font-body text-charcoal/70">A quick Google or Facebook review helps our small local crew more than you know. Thank you!</p>
              <div className="mt-5 flex flex-wrap justify-center gap-3">
              {BRAND.reviewGoogleUrl && (
              <a href={BRAND.reviewGoogleUrl} target="_blank" rel="noreferrer" data-testid="about-review-google"
                className="inline-flex items-center gap-2 bg-white text-charcoal font-heading font-bold rounded-full px-6 py-3 border-2 border-charcoal shadow-brut hover:bg-cream transition-all">
                <Star className="w-5 h-5 fill-sunny text-sunny" /> Leave us a Google review
              </a>
            )}
              <a href={BRAND.reviewFbUrl} target="_blank" rel="noreferrer" data-testid="about-review-fb"
                className="inline-flex items-center gap-2 bg-[#1877F2] text-white font-heading font-bold rounded-full px-6 py-3 border-2 border-charcoal shadow-brut hover:brightness-110 transition-all">
                <Facebook className="w-5 h-5" /> Leave us a Facebook review
              </a>
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
